/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import { useState } from 'react';

import { css, cssMap, jsx } from '@compiled/react';
import { defineMessages, FormattedMessage } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cx } from '@atlaskit/css';
import { SmartCardProvider } from '@atlaskit/link-provider';
import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { LinkListCompact } from './link-list-compact';
import { LinkListFull } from './link-list-full';

const messages = defineMessages({
	link_list_expand: {
		id: 'smart_knowledge_card.link_list_expand',
		defaultMessage: 'Show more',
		description: 'Text for the link list show more button',
	},
	link_list_collapse: {
		id: 'smart_knowledge_card.link_list_collapse',
		defaultMessage: 'Show less',
		description: 'Text for the link list show less button',
	},
});

interface LinkListProps {
	urls: string[];
}

const styles = cssMap({
	root: {
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
	},
	expandWrapper: {
		height: '40px',
	},
	borderBottomRounded: {
		borderBottomLeftRadius: token('radius.medium'),
		borderBottomRightRadius: token('radius.medium'),
	},
});

const containerRoot = css({
	width: '100%',
	containerType: 'inline-size',
	containerName: 'link-list-knowledge-card-container',
});

const showCompact = css({
	display: 'block',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-container-queries
	'@container link-list-knowledge-card-container (min-width: 481px)': {
		display: 'none',
	},
});

const showFull = css({
	display: 'none',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-container-queries
	'@container link-list-knowledge-card-container (min-width: 481px)': {
		display: 'block',
	},
});

export const DEFAULT_MAX_SOURCES = 4;

export const LinkList = ({ urls }: LinkListProps) => {
	const numUrls = urls.length;
	const canExpand = numUrls > DEFAULT_MAX_SOURCES;
	const [isExpanded, setIsExpanded] = useState(false);

	// if isExpanded, show all urls
	// otherwise show DEFAULT_MAX_SOURCES
	// (if numUrls < DEFAULT_MAX_SOURCES, we still show all urls)
	const visibleLinks = isExpanded ? urls : urls.slice(0, DEFAULT_MAX_SOURCES);

	return (
		<div css={containerRoot}>
			<SmartCardProvider>
				<Stack space="space.0">
					<>
						<div css={showCompact}>
							{/* borderBottom of last element in list should be rounded if show more/less element is not rendered */}
							<LinkListCompact visibleLinks={visibleLinks} borderBottomRounded={!canExpand} />
						</div>
						<div css={showFull}>
							{/* borderBottom of last element in list should be rounded if show more/less element is not rendered */}
							<LinkListFull visibleLinks={visibleLinks} borderBottomRounded={!canExpand} />
						</div>
					</>

					{numUrls > DEFAULT_MAX_SOURCES && (
						<Inline
							xcss={cx(styles.root, styles.expandWrapper, styles.borderBottomRounded)}
							alignBlock="center"
						>
							<Button appearance="subtle" onClick={() => setIsExpanded(!isExpanded)}>
								<Text color="color.text.subtlest">
									{isExpanded ? (
										<FormattedMessage {...messages.link_list_collapse} />
									) : (
										<FormattedMessage {...messages.link_list_expand} />
									)}
								</Text>
							</Button>
						</Inline>
					)}
				</Stack>
			</SmartCardProvider>
		</div>
	);
};
